import { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Rocket, TrendUp, Bank, ChartLineUp, Globe, Handshake, Sparkle } from "@phosphor-icons/react";
import { AFRICA_COUNTRIES, SECTORS, CURRENCIES } from "@/lib/data";

const ICON_MAP = {
  rocket: Rocket, trending: TrendUp, building: Bank,
  "chart-up": ChartLineUp, globe: Globe, handshake: Handshake,
};

const ACCENT_MAP = {
  teal: "bg-teal-soft text-teal",
  gold: "bg-gold-soft text-gold",
  navy: "bg-secondary text-primary",
};

export default function WorkflowStart() {
  const { workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const presetWs = searchParams.get("workspace");  // stable primitive — safe for deps
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [form, setForm] = useState({ workspace_id: "__new__", workspace_name: "", intake: {} });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, ws] = await Promise.all([
          api.get("/workflows/catalog"),
          api.get("/workspaces"),
        ]);
        if (cancelled) return;
        const m = cat.data.workflows.find((w) => w.id === workflowId);
        if (!m) { toast.error("Unknown workflow"); navigate("/dashboard"); return; }
        setMeta(m);
        setWorkspaces(ws.data);
        const intake = {};
        m.intake_fields.forEach((f) => {
          if (f.default !== undefined) intake[f.key] = f.default;
          if (f.type === "countries") intake[f.key] = [];
          if (f.type === "currency_select" && !intake[f.key]) intake[f.key] = "USD";
        });
        setForm((s) => ({
          ...s,
          intake,
          workspace_id: presetWs && ws.data.find((w) => w.id === presetWs) ? presetWs : "__new__",
        }));
      } catch (e) {
        if (!cancelled) toast.error("Failed to load workflow");
      }
    })();
    return () => { cancelled = true; };
  }, [workflowId, navigate, presetWs]);

  const setIntake = (k, v) => setForm((s) => ({ ...s, intake: { ...s.intake, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    // validation
    const missing = meta.intake_fields
      .filter((f) => f.required && (form.intake[f.key] === undefined || form.intake[f.key] === "" || (Array.isArray(form.intake[f.key]) && form.intake[f.key].length === 0)))
      .map((f) => f.label);
    if (missing.length) {
      toast.error(`Required: ${missing.join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        workflow_id: workflowId,
        intake: form.intake,
        workspace_id: form.workspace_id === "__new__" ? null : form.workspace_id,
        workspace_name: form.workspace_id === "__new__" ? (form.workspace_name || meta.label) : null,
      };
      const { data } = await api.post("/workflows/start", payload);
      toast.success("Workflow launched");
      navigate(`/workflows/run/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const Icon = ICON_MAP[meta.icon] || Sparkle;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="workflow-start-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> Dashboard
      </Link>

      <div className="flex items-start gap-4 mb-8">
        <div className={`h-14 w-14 rounded-xl grid place-items-center ${ACCENT_MAP[meta.accent] || ACCENT_MAP.navy}`}>
          <Icon size={26} weight="duotone" />
        </div>
        <div>
          <div className="text-overline text-primary mb-1">Workflow</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight" data-testid="workflow-title">{meta.label}</h1>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">{meta.description}</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-7" data-testid="workflow-intake-form">
        {/* Workspace selector */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-overline text-muted-foreground mb-3">Workspace</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={form.workspace_id}
              onChange={(e) => setForm((s) => ({ ...s, workspace_id: e.target.value }))}
              className="bg-background border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="workflow-workspace-select"
            >
              <option value="__new__">+ Create new workspace</option>
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            {form.workspace_id === "__new__" && (
              <input
                value={form.workspace_name}
                onChange={(e) => setForm((s) => ({ ...s, workspace_name: e.target.value }))}
                placeholder={`e.g. ${meta.label} project`}
                className="bg-background border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="workflow-workspace-name"
              />
            )}
          </div>
        </div>

        {/* Intake fields */}
        <div className="space-y-5">
          {meta.intake_fields.map((f) => (
            <Field key={f.key} field={f} value={form.intake[f.key]} onChange={(v) => setIntake(f.key, v)} />
          ))}
        </div>

        {/* Steps preview */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-overline text-muted-foreground mb-3">Deliverables ({meta.steps.length})</div>
          <ol className="space-y-2.5">
            {meta.steps.map((s, i) => (
              <li key={s.key} className="flex items-start gap-3 text-sm">
                <span className="font-mono text-xs text-muted-foreground mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <div className="font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground">→ {s.deliverable}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-4 text-xs text-muted-foreground">
            Estimated time: ~{meta.estimated_minutes} minutes · Powered by Claude Sonnet 4.5
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2">
          <button type="button" onClick={() => navigate("/dashboard")} className="px-4 py-2.5 text-sm border border-border rounded-lg hover:bg-secondary">
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 card-elev"
            data-testid="workflow-launch-btn"
          >
            {submitting ? "Launching…" : <>Launch workflow <ArrowRight weight="bold" size={14} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}


function Field({ field, value, onChange }) {
  const label = (
    <label className="text-overline text-muted-foreground block mb-2" data-testid={`field-label-${field.key}`}>
      {field.label}{field.required && <span className="text-rose-500 ml-1">*</span>}
    </label>
  );

  if (field.type === "text") {
    return (
      <div>{label}
        <input
          type="text" value={value || ""} placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div>{label}
        <input
          type="number" value={value || ""} placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>{label}
        <textarea
          rows={3} value={value || ""} placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>{label}
        <select
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`field-${field.key}`}
        >
          <option value="">Select…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === "sector") {
    return (
      <div>{label}
        <select
          value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`field-${field.key}`}
        >
          <option value="">Select sector…</option>
          {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    );
  }

  if (field.type === "countries") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>{label}
        <CountriesPicker selected={arr} onChange={onChange} placeholder={field.placeholder} testid={`field-${field.key}`} />
      </div>
    );
  }

  if (field.type === "currency") {
    return (
      <div>{label}
        <input
          type="number" min={0} value={value || ""} placeholder={field.placeholder || "e.g. 500000"}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`field-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "currency_select") {
    return (
      <div>{label}
        <select
          value={value || "USD"} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid={`field-${field.key}`}
        >
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    );
  }

  return null;
}

function CountriesPicker({ selected, onChange, placeholder, testid }) {
  const [q, setQ] = useState("");
  // AFRICA_COUNTRIES is a string[] — every entry IS the country name.
  // Defensive: tolerate undefined entries and undefined `q` / `selected`.
  const safeSelected = Array.isArray(selected) ? selected : [];
  const query = (q ?? "").toLowerCase();
  const filtered = (AFRICA_COUNTRIES || [])
    .filter((c) => typeof c === "string" && c.length > 0)
    .filter((c) => c.toLowerCase().includes(query) && !safeSelected.includes(c))
    .slice(0, 8);

  const add = (name) => {
    if (!name) return;
    onChange([...safeSelected, name]);
    setQ("");
  };
  const remove = (name) => onChange(safeSelected.filter((s) => s !== name));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {safeSelected.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 bg-teal-soft text-teal rounded-full px-2.5 py-1 text-xs font-medium">
            {s}
            <button type="button" onClick={() => remove(s)} className="hover:text-rose-600" aria-label={`Remove ${s}`}>×</button>
          </span>
        ))}
      </div>
      <input
        type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder || "Search country…"}
        className="w-full bg-card border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        data-testid={testid ? `${testid}-input` : undefined}
      />
      {q && filtered.length > 0 && (
        <div className="mt-1 bg-card border border-border rounded-md max-h-44 overflow-y-auto">
          {filtered.map((c) => (
            <button key={c} type="button" onClick={() => add(c)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary"
              data-testid={testid ? `${testid}-option-${c.toLowerCase().replace(/\s/g, "-")}` : undefined}>
              {c}
            </button>
          ))}
        </div>
      )}
      {q && filtered.length === 0 && (
        <div className="mt-1 text-xs text-muted-foreground px-1 py-1">No matching country.</div>
      )}
    </div>
  );
}
